import React from 'react';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import LaddaButton, { SLIDE_DOWN } from 'react-ladda';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import Input from '../common/input';
import Textarea from '../common/textarea';
import {
  requiredField,
  emailField,
  numericFieldWhereNotStartWithZero,
  phoneField,
} from '../../helpers/validator';

import DynamicBookingCalendar from './dynamic-booking-calendar';

let DynamicServiceDetailsForm = ({
  handleSubmit,
  isLoading,
  errorMessage,
  schedules,
  slots,
  action,
}) => {
  const { t } = useTranslation();
  // This filtering is quite weird for multiple reasons, but it's basically all because the idea of "DATE" and "TIME" is not represented in a sane way.
  // MIOO stores both dates and time-of-day as timestamps, which is not correct for either of them.
  // * A date should not be a timestamp, because it does not have the concept of a timezone. A date should instead always be the date it represents. "10 oct" is always 10 oct, doesn't matter where you are.
  // * A time-of-day should not be a timestamp, because 10:15 should always mean 10:15. It should depend on timezone or daylight savings or similar.
  // For example, when MIOO sets up service time slots for their shops, they assign opening hours. The GUI does not deal with daylight saving (why should it); the user siply says the shop is
  // open from 08:00 to 11:00 for example. No one cares about day ligth savings. But this is stored as "YYYY-MM-DD 06:00:ss" and "YYYY-MM-DD 09:00:ss" in the database where the letters represent the values of
  // the current time and the 06 and 09 arethe entered values, adjusted by local time offset (supposedly). The reason they are off by 2 is most likely because they were set during swedish summer time.
  // This will break if new times are added in swedish winter time (or any other time zone). It's also CRAZILY confusing that the year, month, date and second is saved inside a "time-of-day" variable.
  // All in all, below here is a filtering function which intends to work out which service slots are in the future compared to "right now", by doing some hacky concatenation of MIOO "date" and "time-of-day"
  // and adjusting for the two hour offset and ignoring timezone. This is horrible and will not work under all circumstances, but the proper fix cannot be done here but must be done in how time-of-day is handled.
  const now = moment();
  const now_timestamp = now.format('YYYY-MM-DD HH');
  const slotsLookup = Object.fromEntries(slots.map((x) => [x.id, x]));
  const futureSchedules = schedules
    .map((x) => ({
      id: x.service_slot_id,
      service_date: moment(x.service_date).format('YYYY-MM-DD'),
      name: slotsLookup[x.service_slot_id].name,
      service_end_timestamp:
        moment(x.service_date).format('YYYY-MM-DD') +
        ' ' +
        moment(slotsLookup[x.service_slot_id].end_time).add(2, 'hours').utc().format('HH'),
    }))
    .filter((x) => x.service_end_timestamp > now_timestamp)
    .map((x) => ({
      id: x.id,
      service_date: x.service_date,
      name: x.name,
    }));

  if (futureSchedules.length === 0 || slots.length === 0) {
    return <div style={{ color: 'red' }}>{t('noAvailableSlots', 'No available time slots')}</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <DynamicBookingCalendar schedules={futureSchedules} />
      <Field
        type="text"
        name="first_name"
        label={t('name', 'Name')}
        className="form-control"
        placeholder={t('enter_first_name', 'Enter your first name')}
        component={Input}
      />
      <Field
        type="text"
        name="last_name"
        label={t('lastName', 'Last Name')}
        className="form-control"
        placeholder={t('enter_last_name', 'Enter your last name')}
        component={Input}
      />
      <Field
        type="text"
        name="email"
        label={t('email', 'Email')}
        className="form-control"
        placeholder={t('enter_email', 'Enter your email')}
        component={Input}
      />
      <hr className="line" />
      <Field
        name="note"
        label={t('noteLabel', 'Note')}
        className="form-control"
        placeholder={t('notePlaceholder', 'Brief description (optional)')}
        component={Textarea}
      />
      {action === 'standard' && (
        <>
          <hr className="line" />
          <Field
            type="tel"
            name="phone_number"
            label={
              <>
                {t('phoneNumber', 'Your phone number')}
                <br />
                <span className="format-label">
                  ({t('phoneFormat', 'without 0 at the start and without spaces')})
                </span>
              </>
            }
            countryCode="+46"
            className="form-control"
            placeholder={t('phonePlaceholder', 'Mobile number')}
            component={Input}
          />
        </>
      )}
      <hr className="line" />
      <LaddaButton className="btn w-100 submit-button" disabled={isLoading} data-style={SLIDE_DOWN}>
        {t('book', 'Book')}
      </LaddaButton>
      <p className="text-danger text-center">{errorMessage}</p>
    </form>
  );
};

const serviceDetailsFormValidator = (values, props) => {
  const errors = {};
  requiredField(values, errors, 'first_name', 'VALIDATION.FIRST-NAME.ERROR_REQUIRED');
  requiredField(values, errors, 'last_name', 'VALIDATION.LAST-NAME.ERROR_REQUIRED');
  requiredField(values, errors, 'email', 'VALIDATION.EMAIL.ERROR_REQUIRED');
  /*requiredField(values, errors, 'phone_number', 'VALIDATION.TOS.ERROR_REQUIRED');*/
  // Require phone number only for the default ("standard") action
  if (props?.action === 'standard') {
    requiredField(values, errors, 'phone_number', 'VALIDATION.TOS.ERROR_REQUIRED');
  }
  emailField(values, errors, 'email', 'VALIDATION.EMAIL.WRONG_FORMAT');
  /*numericFieldWhereNotStartWithZero(
    values,
    errors,
    'phone_number',
    'VALIDATION.TOS.WRONG_NUMERIC_FORMAT'
  );
  phoneField(values, errors, 'phone_number', 'VALIDATION.TOS.WRONG_FORMAT');
  */
  if (values.phone_number) {
    numericFieldWhereNotStartWithZero(
      values,
      errors,
      'phone_number',
      'VALIDATION.TOS.WRONG_NUMERIC_FORMAT'
    );
    phoneField(values, errors, 'phone_number', 'VALIDATION.TOS.WRONG_FORMAT');
  }

  return errors;
};

DynamicServiceDetailsForm = reduxForm({
  form: 'service-details',
  validate: serviceDetailsFormValidator,
})(DynamicServiceDetailsForm);

DynamicServiceDetailsForm = connect((state) => ({
  initialValues: state.service.user,
}))(DynamicServiceDetailsForm);

export default DynamicServiceDetailsForm;
